// First-party consented visitor history client.
//
// Design:
//   * Creates a stable `consented_visitor_id` (cvid) ONLY after iubenda
//     purpose 4 (Measurement) is granted. Persists in localStorage.
//   * Creates a `session_id` (sid) that resets after 30 minutes of
//     inactivity. Persists in sessionStorage.
//   * Emits events: pageview (route change), cta_click (any [data-cta]
//     click), buy_now_click, amazon_outbound. Batches to the backend on
//     visibilitychange:hidden / pagehide via sendBeacon.
//   * On consent withdrawal: sends POST /consented/revoke, clears
//     localStorage/sessionStorage, disables the tracker for the rest of
//     the page life. Aggregate all-traffic tracking is unaffected.
//   * Never captures raw IP or full UA — the server derives those from
//     headers and stores only country + UA family.

const LS_CVID = "sd_cvid";
const SS_SID  = "sd_sid";
const SS_SID_LAST = "sd_sid_last";
const SESSION_IDLE_MS = 30 * 60_000;

const state = {
  enabled: false,
  installed: false,
  backendUrl: "",
  cvid: null,
  sid: null,
  user: null,           // {id, email, first_name, surname} once known
  buffer: [],
  sessionStartedAt: 0,
  sessionLastEventAt: 0,
  flushTimer: null,
};

function hasMeasurementConsent() {
  try {
    const cs = (window._iub && window._iub.cs && window._iub.cs.consent) || null;
    if (!cs) return false;
    const purposes = cs.purposes || cs.categories || {};
    return purposes["4"] === true || purposes[4] === true;
  } catch {
    return false;
  }
}

function randHex(n) {
  const a = new Uint8Array(n / 2);
  (window.crypto || window.msCrypto).getRandomValues(a);
  return Array.from(a).map((b) => b.toString(16).padStart(2, "0")).join("");
}

function ensureCvid() {
  try {
    let v = localStorage.getItem(LS_CVID);
    if (!v || !/^[0-9a-f]{16,64}$/.test(v)) {
      v = randHex(24);
      localStorage.setItem(LS_CVID, v);
    }
    return v;
  } catch {
    return null;
  }
}

function ensureSid() {
  try {
    const now = Date.now();
    const last = Number(sessionStorage.getItem(SS_SID_LAST) || 0);
    let sid = sessionStorage.getItem(SS_SID);
    if (!sid || !/^[0-9a-f]{16,64}$/.test(sid) || now - last > SESSION_IDLE_MS) {
      sid = randHex(24);
      sessionStorage.setItem(SS_SID, sid);
      state.sessionStartedAt = now;
    }
    sessionStorage.setItem(SS_SID_LAST, String(now));
    return sid;
  } catch {
    return null;
  }
}

function utmFromLocation() {
  const p = new URLSearchParams(window.location.search || "");
  const utm = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]
    .forEach((k) => { const v = p.get(k); if (v) utm[k] = v.slice(0, 60); });
  return Object.keys(utm).length ? utm : null;
}

function payload(events, extra = {}) {
  return {
    consent_measurement: hasMeasurementConsent(),
    consented_visitor_id: state.cvid,
    session_id: state.sid,
    user_id: state.user?.id || null,
    email: state.user?.email || null,
    first_name: state.user?.first_name || null,
    surname: state.user?.surname || null,
    events,
    ...extra,
  };
}

function send(body, useBeacon = true) {
  if (!state.enabled) return;
  if (!state.backendUrl) return;
  const url = `${state.backendUrl}/api/consented/ingest`;
  const data = JSON.stringify(body);
  try {
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([data], { type: "application/json" }));
    } else {
      fetch(url, {
        method: "POST",
        credentials: "omit",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: data,
      }).catch(() => {});
    }
  } catch { /* noop */ }
}

function enqueue(event) {
  if (!state.enabled) return;
  state.buffer.push({
    ...event,
    ts: new Date().toISOString(),
    path: event.path || window.location.pathname,
    referrer: event.referrer !== undefined ? event.referrer : document.referrer || null,
    utm: event.utm !== undefined ? event.utm : utmFromLocation(),
  });
  state.sessionLastEventAt = Date.now();
  scheduleFlush();
}

function scheduleFlush() {
  if (state.flushTimer) return;
  state.flushTimer = setTimeout(() => {
    state.flushTimer = null;
    flush(false);
  }, 15_000);
}

function flush(useBeacon) {
  if (!state.enabled) return;
  if (state.buffer.length === 0) return;
  const events = state.buffer.splice(0, state.buffer.length);
  send(payload(events), useBeacon);
}

function flushSessionEnd() {
  if (!state.enabled) return;
  const dur = state.sessionStartedAt
    ? Math.max(0, Date.now() - state.sessionStartedAt)
    : 0;
  const events = state.buffer.splice(0, state.buffer.length);
  send(payload(events, { session_end: true, session_duration_ms: dur }), true);
}

// -------------------- Public API --------------------
export function setUser(user) {
  state.user = user
    ? {
        id: user.id || user._id || null,
        email: user.email || null,
        first_name: user.first_name || null,
        surname: user.surname || user.last_name || null,
      }
    : null;
  if (state.enabled) {
    // Emit a light identify event so admins can pin the moment when the
    // anonymous cvid became a named user.
    enqueue({ event: "identify" });
  }
}

export function pageview(path) {
  ensureSid();
  enqueue({ event: "pageview", path: path || window.location.pathname });
}

export function ctaClick({ cta_id, section_id }) {
  ensureSid();
  enqueue({ event: cta_id === "buy_now_main" ? "buy_now_click" : "cta_click",
            cta_id, section_id });
}

export function amazonOutbound({ marketplace, section_id, cta_id }) {
  ensureSid();
  send(payload([{
    event: "amazon_outbound",
    marketplace, section_id, cta_id,
    path: window.location.pathname,
    referrer: document.referrer || null,
    utm: utmFromLocation(),
    ts: new Date().toISOString(),
  }]), true);
}

function revoke() {
  if (!state.cvid) return;
  const url = `${state.backendUrl}/api/consented/revoke`;
  const body = JSON.stringify({ consented_visitor_id: state.cvid });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    } else {
      fetch(url, { method: "POST", credentials: "omit", keepalive: true,
                    headers: { "Content-Type": "application/json" }, body }).catch(() => {});
    }
  } catch { /* noop */ }
  try {
    localStorage.removeItem(LS_CVID);
    sessionStorage.removeItem(SS_SID);
    sessionStorage.removeItem(SS_SID_LAST);
  } catch { /* noop */ }
  state.enabled = false;
  state.cvid = null;
  state.sid = null;
  state.buffer = [];
}

function activate() {
  state.cvid = ensureCvid();
  state.sid = ensureSid();
  if (!state.cvid || !state.sid) return;
  state.enabled = true;
  // Emit an initial pageview for the current route.
  enqueue({ event: "pageview" });
  window.__sdConsentedTracker = { setUser, pageview, ctaClick, amazonOutbound, revoke };
}

export function install(backendUrl) {
  if (state.installed) return;
  state.installed = true;
  state.backendUrl = (backendUrl || "").replace(/\/+$/, "");

  if (hasMeasurementConsent()) {
    activate();
  }
  // React to iubenda emissions arriving AFTER page load.
  document.addEventListener("iubenda_consent_given", () => {
    if (!state.enabled && hasMeasurementConsent()) activate();
  });
  // Withdrawn / preferences saved with purpose 4 turned off.
  document.addEventListener("iubenda_preference_expressed", () => {
    if (state.enabled && !hasMeasurementConsent()) revoke();
  });
  // Generic listener: some iubenda banners dispatch these too.
  document.addEventListener("iubenda_consent_rejected", () => {
    if (state.enabled) revoke();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) flush(true);
  });
  window.addEventListener("pagehide", () => flushSessionEnd());
  window.addEventListener("beforeunload", () => flush(true));

  // Click auto-capture for [data-cta] elements.
  document.addEventListener("click", (evt) => {
    if (!state.enabled) return;
    let n = evt.target;
    while (n && n.nodeType === 1) {
      const cta = n.getAttribute && n.getAttribute("data-cta");
      if (cta) {
        let sec = n;
        while (sec && sec.nodeType === 1) {
          if (sec.getAttribute && sec.getAttribute("data-section")) break;
          sec = sec.parentNode;
        }
        ctaClick({
          cta_id: cta,
          section_id: sec && sec.getAttribute ? sec.getAttribute("data-section") : null,
        });
        return;
      }
      n = n.parentNode;
    }
  }, { passive: true, capture: true });
}
